<script setup lang="ts">
import { useEcho } from '@laravel/echo-vue'

const { $api, $ts } = useNuxtApp()

const { compact } = useTournamentHelpers()

const isStartingConsolationKO = ref(false)

const props = withDefaults(defineProps<{
  group: 'a' | 'b'
  boards: Board[]
  displayOnly?: boolean
  tournament: Tournament | ManageTournament
  hideBye?: boolean
  refreshTournament?: (tournament?: ManageTournament) => Promise<void>
  onGameClick?: (game: Game) => void
}>(), { displayOnly: true, hideBye: false })

const { data: roundGames } = await useApi('/tournaments/{tournament}/elimination',
  {
    path: {
      tournament: props.tournament.id,
    },
    query: {
      group: props.group,
    },
    lazy: true,
    key: `tournament-elimination-${props.tournament.id}-group-${props.group}`,
    deep: true,
  },
)

if (!props.tournament.complete) {
  const channelType = props.displayOnly ? 'tournaments' : 'events'
  const id = props.displayOnly ? props.tournament.id : props.tournament.event_id
  useEcho<Game>(`${channelType}.${id}`, '.App\\Events\\GameUpdatedEvent', (newData) => {
    if (roundGames.value && newData.elimination_group && newData.elimination_round) {
      const index = roundGames.value[newData.elimination_round]!.games.findIndex(item => item.id === newData.id)

      roundGames.value[newData.elimination_round]!.games[index] = newData
    }
  })
}

const amountOfRounds = computed(() => {
  return Object.keys(roundGames?.value ?? {}).length
})

function getOngoingGamesForRound(round: { games: Game[] }) {
  return round.games.filter(game => game.board?.id).length
}

function getPlayedGames(round: { games: Game[] }) {
  return round.games.filter(game => game.concluded_at).length
}

function roundIsOngoing(round: { total_games: number, games: Game[] }) {
  const remainingGames = round.total_games - getPlayedGames(round)
  return remainingGames <= round.total_games && remainingGames !== 0
}

async function startConsolationKO() {
  if (props.displayOnly || isStartingConsolationKO.value) {
    return
  }

  isStartingConsolationKO.value = true

  await $api('/tournaments/{tournament}/start-consolation-ko', {
    method: 'POST',
    path: {
      tournament: props.tournament.id,
    },
  })
    .then(async (data) => {
      useSonner.success($ts('Consolation knockout has been started!'))
      if (props.refreshTournament) {
        await props.refreshTournament(data as ManageTournament)
      }
    })
    .catch((error) => {
      console.error('Failed to start consolation KO:', error)
    })
    .finally(() => {
      isStartingConsolationKO.value = false
    })
}
</script>

<template>
  <div
    v-if="roundGames"
    class="!flex !flex-grow !flex-col w-full relative justify-items-stretch"
    :style="{ width: (amountOfRounds * 250) + 'px' }"
  >
    <UiAlert
      v-if="!displayOnly && !tournament.consolation_started && tournament.elimination_phase_b_round && !tournament.with_group_phase && (roundIsOngoing(roundGames[1]!) || roundIsOngoing(roundGames[2]!))"
      variant="info"
      :title="$t('Consolation knockout cannot be started yet.')"
      :description="$t('Consolation knockout can start once the first 2 rounds are played in the main knockout.')"
    />
    <div
      v-if="!displayOnly && !tournament.consolation_started && roundGames && !tournament.with_group_phase && group == 'a' && !roundIsOngoing(roundGames[1]!) && !roundIsOngoing(roundGames[2]!) && tournament.elimination_phase_b_round"
      class="flex flex-col gap-4 p-4"
    >
      <UiAlert :title="$t('Elimination round 1 and 2 are complete. Consolation KO can now be played')" />
      <UiButton
        :text="$t('Start consolation KO')"
        variant="success"
        :loading="isStartingConsolationKO"
        :disabled="isStartingConsolationKO"
        @click="startConsolationKO"
      />
    </div>
    <div class="flex flex-row gap-2">
      <UiButton
        variant="ghost"
        class="self-start"
        size="sm"
        :text="compact ? $t('Expand') : $t('Compact')"
        :icon="compact ? 'tabler:arrow-down' : 'tabler:arrow-up'"
        @click="compact = !compact"
      />
      <GamescreenPrinterAndAutoplayStatus :tournament="tournament as ManageTournament" />
    </div>

    <div class="flex left-0 right-0 z-50 bg-gray-100/50 dark:bg-black/50 backdrop-blur sticky top-0">
      <div
        v-for="(round, roundIndex) in roundGames"
        :key="roundIndex"
        class="w-[250px] flex flex-col"
      >
        <template v-if="round.games.length === 0">
          <UiAlert :title="$t('Waiting for game')" />
        </template>
        <template v-else>
          <div class="!flex !flex-row w-full">
            <div class="!flex !flex-grow w-full overflow-hidden">
              <h3 class="p-1 text-md overflow-hidden max-w-full">
                <span class="w-full overflow-hidden truncate mr-2">{{ round.label }}</span>
                <span class="text-xs">{{ getPlayedGames(round) + '/' + round.total_games }}</span>
                <span class="text-xs flex items-center">
                  {{ $t('Best of {phAmount} legs', { phAmount: round.games[0]!.best_of }) }}
                  ({{ round.games[0]!.starting_score }})
                </span>
              </h3>
            </div>
            <div class="flex items-center gap-2">
              <template v-if="roundIsOngoing(round)">
                <template v-if="getOngoingGamesForRound(round)">
                  <UiBadge
                    variant="success"
                    class="text-xs truncate overflow-hidden"
                  >
                    {{ getOngoingGamesForRound(round) }} {{ $t('Ongoing') }}
                  </UiBadge>
                </template>
              </template>
              <template v-else-if="getPlayedGames(round) === round.total_games">
                <UiBadge
                  class="px-1"
                  variant="success"
                >
                  <Icon name="tabler-check" />
                </UiBadge>
              </template>
            </div>
          </div>
          <UiProgress :model-value="(getPlayedGames(round) / round.total_games) * 100" />
        </template>
      </div>
    </div>
    <div class="flex flex-row divide-x-[1px] divide-gray-100 dark:divide-gray-700">
      <div
        v-for="(round, roundIndex) in roundGames"
        :key="roundIndex"
        class="flex flex-col w-[250px] p-3"
      >
        <div
          v-for="(game, index) in round.games"
          :key="game.id"
          class="!flex relative max-w-full"
          :class="{
            'opacity-0': hideBye && game.has_bye,
            'items-start': compact,
            'items-center !flex-1': !compact,
            'dc-match-connect-next-odd': !compact && (index % 2 === 0) && (parseInt(roundIndex.toString()) !== amountOfRounds),
            'dc-match-connect-next-even': !compact && (index % 2 !== 0) && (parseInt(roundIndex.toString()) !== amountOfRounds),
          }"
        >
          <div
            class="relative w-full justify-center flex flex-col py-0.5"
            :class="{
              'dc-match-connect-previous': !compact && parseInt(roundIndex.toString()) !== 1,
            }"
          >
            <GamescreenGameCard
              :boards="boards"
              :game="game"
              :for-display="displayOnly"
              :compact="compact"
              :tournament="tournament"
              :on-game-click="onGameClick"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
